import {
  DASHBOARD_ARTIFACT_SPEC_V2_VERSION,
  DashboardArtifactSpecV2,
  DashboardChartWidgetV2,
  DashboardKpiWidgetV2,
  DashboardTableWidgetV2,
  DashboardTextWidget,
  dashboardArtifactSpecV2For,
  type DashboardEditIssue,
  type DashboardEditOperation,
  type DashboardLayoutRowV2,
  type DashboardWidgetDataSource,
  type DashboardWidgetPatchKey,
  type DashboardWidgetPosition,
  type DashboardWidgetV2,
  type MetricDefinition,
  type MetricQuery,
} from "@t3tools/contracts";
import { Cause, Exit, Schema, SchemaIssue } from "effect";

/**
 * Pure helpers for dashboard edit operations, shared by the web app and the
 * control plane: apply a batch to a v2 spec, invert it for undo, and describe
 * it in plain language. Spec validity is the contract's own check
 * (`DashboardArtifactSpecV2`, or `dashboardArtifactSpecV2For(metrics)` when a
 * metric catalog is given), never a copy of it.
 */

export type DashboardEditOptions = {
  /** Checks metric widgets against these definitions and names metrics and splits in summaries. */
  readonly metrics?: ReadonlyArray<MetricDefinition>;
};

export type ApplyEditsResult =
  | { readonly spec: DashboardArtifactSpecV2 }
  | { readonly issues: ReadonlyArray<DashboardEditIssue> };

export type InvertEditsResult =
  | { readonly ops: ReadonlyArray<DashboardEditOperation> }
  | { readonly issues: ReadonlyArray<DashboardEditIssue> };

type Spec = DashboardArtifactSpecV2;
type Row = DashboardLayoutRowV2;
type StepIssue = { readonly path: ReadonlyArray<string | number>; readonly message: string };

const DECODE_OPTIONS = { errors: "all", onExcessProperty: "error" } as const;
const formatIssues = SchemaIssue.makeFormatterStandardSchemaV1();

/** The keys each widget kind takes, read from the contract's widget schemas. */
const WIDGET_KEYS: Record<DashboardWidgetV2["kind"], ReadonlySet<string>> = {
  kpi: new Set(Object.keys(DashboardKpiWidgetV2.fields)),
  chart: new Set(Object.keys(DashboardChartWidgetV2.fields)),
  table: new Set(Object.keys(DashboardTableWidgetV2.fields)),
  text: new Set(Object.keys(DashboardTextWidget.fields)),
};

// Layout -------------------------------------------------------------------

type Located = { readonly widget: DashboardWidgetV2; readonly at: DashboardWidgetPosition };

function locate(rows: ReadonlyArray<Row>, widgetId: string): Located | undefined {
  for (const [row, { widgets }] of rows.entries()) {
    const index = widgets.findIndex((widget) => widget.id === widgetId);
    if (index !== -1) return { widget: widgets[index]!, at: { row, index } };
  }
  return undefined;
}

/** Takes a widget out of the layout, deleting its row when that empties it. */
function take(rows: ReadonlyArray<Row>, at: DashboardWidgetPosition) {
  const row = rows[at.row]!;
  const widgets = row.widgets.filter((_, index) => index !== at.index);
  const emptied = widgets.length === 0;
  const next = emptied
    ? rows.filter((_, index) => index !== at.row)
    : rows.map((candidate, index) => (index === at.row ? { ...row, widgets } : candidate));
  const { widgets: _widgets, ...rowFields } = row;
  return { rows: next, emptiedRow: emptied ? rowFields : undefined };
}

function insert(
  rows: ReadonlyArray<Row>,
  widget: DashboardWidgetV2,
  at: DashboardWidgetPosition,
  newRow: { readonly title?: string } | undefined,
): ReadonlyArray<Row> | StepIssue {
  if (newRow) {
    if (at.row > rows.length) {
      return { path: ["at", "row"], message: `There is no row ${at.row} to insert before` };
    }
    return [...rows.slice(0, at.row), { ...newRow, widgets: [widget] }, ...rows.slice(at.row)];
  }
  const row = rows[at.row];
  if (!row) return { path: ["at", "row"], message: `There is no row ${at.row}` };
  if (at.index > row.widgets.length) {
    return { path: ["at", "index"], message: `Row ${at.row} has no position ${at.index}` };
  }
  const widgets = [...row.widgets.slice(0, at.index), widget, ...row.widgets.slice(at.index)];
  return rows.map((candidate, index) => (index === at.row ? { ...row, widgets } : candidate));
}

function replaceWidget(
  rows: ReadonlyArray<Row>,
  at: DashboardWidgetPosition,
  widget: DashboardWidgetV2,
) {
  return rows.map((row, rowIndex) =>
    rowIndex === at.row
      ? {
          ...row,
          widgets: row.widgets.map((current, index) => (index === at.index ? widget : current)),
        }
      : row,
  );
}

function withoutKey<T extends object>(value: T, key: string): T {
  const copy = { ...value } as Record<string, unknown>;
  delete copy[key];
  return copy as T;
}

function insertAt<T>(
  items: ReadonlyArray<T>,
  item: T,
  index: number | undefined,
): ReadonlyArray<T> {
  const at = index ?? items.length;
  return [...items.slice(0, at), item, ...items.slice(at)];
}

// Steps --------------------------------------------------------------------

type Step = { readonly spec: Spec; readonly inverse: DashboardEditOperation };

/** Applies one operation without validating the whole spec, and returns its inverse. */
function step(spec: Spec, op: DashboardEditOperation): Step | StepIssue {
  const missingWidget = (widgetId: string): StepIssue => ({
    path: ["widgetId"],
    message: `There is no widget "${widgetId}"`,
  });
  switch (op.op) {
    case "add_widget": {
      if (locate(spec.rows, op.widget.id)) {
        return { path: ["widget", "id"], message: `A widget "${op.widget.id}" already exists` };
      }
      const rows = insert(spec.rows, op.widget, op.at, op.newRow);
      if (!Array.isArray(rows)) return rows as StepIssue;
      return {
        spec: { ...spec, rows: rows as Spec["rows"] },
        inverse: { op: "remove_widget", widgetId: op.widget.id },
      };
    }
    case "remove_widget": {
      const found = locate(spec.rows, op.widgetId);
      if (!found) return missingWidget(op.widgetId);
      const taken = take(spec.rows, found.at);
      return {
        spec: { ...spec, rows: taken.rows as Spec["rows"] },
        inverse: {
          op: "add_widget",
          widget: found.widget,
          at: found.at,
          ...(taken.emptiedRow ? { newRow: taken.emptiedRow } : {}),
        },
      };
    }
    case "move_widget": {
      const found = locate(spec.rows, op.widgetId);
      if (!found) return missingWidget(op.widgetId);
      const taken = take(spec.rows, found.at);
      const rows = insert(taken.rows, found.widget, op.to, op.newRow);
      if (!Array.isArray(rows)) {
        const issue = rows as StepIssue;
        return { ...issue, path: ["to", ...issue.path.slice(1)] };
      }
      return {
        spec: { ...spec, rows: rows as Spec["rows"] },
        inverse: {
          op: "move_widget",
          widgetId: op.widgetId,
          to: found.at,
          ...(taken.emptiedRow ? { newRow: taken.emptiedRow } : {}),
        },
      };
    }
    case "update_widget": {
      const found = locate(spec.rows, op.widgetId);
      if (!found) return missingWidget(op.widgetId);
      const unset = op.unset ?? [];
      const allowed = WIDGET_KEYS[found.widget.kind];
      const setKeys = Object.keys(op.set) as DashboardWidgetPatchKey[];
      for (const key of [...setKeys, ...unset]) {
        if (!allowed.has(key)) {
          return {
            path: [setKeys.includes(key) ? "set" : "unset", key],
            message: `A ${found.widget.kind} widget has no "${key}"`,
          };
        }
      }
      const both = setKeys.find((key) => unset.includes(key));
      if (both) return { path: ["unset"], message: `"${both}" is both set and unset` };

      const before = found.widget as Record<string, unknown>;
      let next: Record<string, unknown> = { ...before, ...op.set };
      for (const key of unset) next = withoutKey(next, key);
      const inverseSet: Record<string, unknown> = {};
      const inverseUnset: DashboardWidgetPatchKey[] = [];
      for (const key of [...setKeys, ...unset]) {
        if (key in before) inverseSet[key] = before[key];
        else if (setKeys.includes(key)) inverseUnset.push(key);
      }
      return {
        spec: {
          ...spec,
          rows: replaceWidget(spec.rows, found.at, next as DashboardWidgetV2) as Spec["rows"],
        },
        inverse: {
          op: "update_widget",
          widgetId: op.widgetId,
          set: inverseSet,
          ...(inverseUnset.length > 0 ? { unset: inverseUnset } : {}),
        },
      };
    }
    case "resize_widget": {
      const found = locate(spec.rows, op.widgetId);
      if (!found) return missingWidget(op.widgetId);
      const widget =
        op.span === null ? withoutKey(found.widget, "span") : { ...found.widget, span: op.span };
      return {
        spec: { ...spec, rows: replaceWidget(spec.rows, found.at, widget) as Spec["rows"] },
        inverse: { op: "resize_widget", widgetId: op.widgetId, span: found.widget.span ?? null },
      };
    }
    case "set_title":
      return {
        spec: { ...spec, title: op.title },
        inverse: { op: "set_title", title: spec.title },
      };
    case "set_description":
      return {
        spec:
          op.description === null
            ? withoutKey(spec, "description")
            : { ...spec, description: op.description },
        inverse: { op: "set_description", description: spec.description ?? null },
      };
    case "add_parameter": {
      if (spec.parameters.some((parameter) => parameter.id === op.parameter.id)) {
        return {
          path: ["parameter", "id"],
          message: `A parameter "${op.parameter.id}" already exists`,
        };
      }
      if (op.index !== undefined && op.index > spec.parameters.length) {
        return { path: ["index"], message: `There is no parameter position ${op.index}` };
      }
      return {
        spec: { ...spec, parameters: insertAt(spec.parameters, op.parameter, op.index) },
        inverse: { op: "remove_parameter", parameterId: op.parameter.id },
      };
    }
    case "update_parameter": {
      const index = spec.parameters.findIndex((parameter) => parameter.id === op.parameter.id);
      if (index === -1) {
        return { path: ["parameter", "id"], message: `There is no parameter "${op.parameter.id}"` };
      }
      return {
        spec: {
          ...spec,
          parameters: spec.parameters.map((parameter, at) =>
            at === index ? op.parameter : parameter,
          ),
        },
        inverse: { op: "update_parameter", parameter: spec.parameters[index]! },
      };
    }
    case "remove_parameter": {
      const index = spec.parameters.findIndex((parameter) => parameter.id === op.parameterId);
      if (index === -1) {
        return { path: ["parameterId"], message: `There is no parameter "${op.parameterId}"` };
      }
      return {
        spec: { ...spec, parameters: spec.parameters.filter((_, at) => at !== index) },
        inverse: { op: "add_parameter", parameter: spec.parameters[index]!, index },
      };
    }
    case "add_dataset": {
      if (spec.datasets.some((dataset) => dataset.id === op.dataset.id)) {
        return { path: ["dataset", "id"], message: `A dataset "${op.dataset.id}" already exists` };
      }
      if (op.index !== undefined && op.index > spec.datasets.length) {
        return { path: ["index"], message: `There is no dataset position ${op.index}` };
      }
      return {
        spec: { ...spec, datasets: insertAt(spec.datasets, op.dataset, op.index) },
        inverse: { op: "remove_dataset", datasetId: op.dataset.id },
      };
    }
    case "update_dataset": {
      const index = spec.datasets.findIndex((dataset) => dataset.id === op.dataset.id);
      if (index === -1) {
        return { path: ["dataset", "id"], message: `There is no dataset "${op.dataset.id}"` };
      }
      return {
        spec: {
          ...spec,
          datasets: spec.datasets.map((dataset, at) => (at === index ? op.dataset : dataset)),
        },
        inverse: { op: "update_dataset", dataset: spec.datasets[index]! },
      };
    }
    case "remove_dataset": {
      const index = spec.datasets.findIndex((dataset) => dataset.id === op.datasetId);
      if (index === -1) {
        return { path: ["datasetId"], message: `There is no dataset "${op.datasetId}"` };
      }
      return {
        spec: { ...spec, datasets: spec.datasets.filter((_, at) => at !== index) },
        inverse: { op: "add_dataset", dataset: spec.datasets[index]!, index },
      };
    }
  }
}

type Replay =
  | { readonly spec: Spec; readonly inverses: ReadonlyArray<DashboardEditOperation> }
  | { readonly issues: ReadonlyArray<DashboardEditIssue> };

function replay(before: Spec, ops: ReadonlyArray<DashboardEditOperation>): Replay {
  let spec = before;
  const inverses: DashboardEditOperation[] = [];
  for (const [opIndex, op] of ops.entries()) {
    const result = step(spec, op);
    if ("message" in result) return { issues: [{ opIndex, ...result }] };
    spec = result.spec;
    inverses.push(result.inverse);
  }
  return { spec, inverses };
}

// Validation ---------------------------------------------------------------

const catalogSchemas = new WeakMap<
  ReadonlyArray<MetricDefinition>,
  ReturnType<typeof dashboardArtifactSpecV2For>
>();

function specSchema(options: DashboardEditOptions | undefined) {
  const metrics = options?.metrics;
  if (!metrics) return DashboardArtifactSpecV2;
  let schema = catalogSchemas.get(metrics);
  if (!schema) {
    schema = dashboardArtifactSpecV2For(metrics);
    catalogSchemas.set(metrics, schema);
  }
  return schema;
}

function toPathKey(segment: unknown): string | number {
  const key =
    typeof segment === "object" && segment !== null && "key" in segment
      ? (segment as { key: unknown }).key
      : segment;
  return typeof key === "number" ? key : String(key);
}

/** Decodes a whole v2 spec, returning its issues by path when it is invalid. */
export function validateSpecV2(spec: unknown, options?: DashboardEditOptions): ApplyEditsResult {
  const decoded = Schema.decodeUnknownExit(specSchema(options))(spec, DECODE_OPTIONS);
  if (Exit.isSuccess(decoded)) return { spec: decoded.value };
  const error = Cause.squash(decoded.cause);
  if (!Schema.isSchemaError(error)) return { issues: [{ path: [], message: String(error) }] };
  return {
    issues: formatIssues(error.issue).issues.map((issue) => ({
      path: (issue.path ?? []).map(toPathKey),
      message: issue.message,
    })),
  };
}

// Public API ---------------------------------------------------------------

/**
 * The starting point of a dashboard that does not exist yet. It has no rows,
 * so it is not a valid spec by itself; the first edits must add a widget.
 */
export function newDashboardSpecV2(title: string): Spec {
  return {
    specVersion: DASHBOARD_ARTIFACT_SPEC_V2_VERSION,
    title,
    parameters: [],
    datasets: [],
    rows: [],
    definitions: [],
  };
}

/**
 * Applies `ops` in order. Returns the new spec when every op applies and the
 * result is a valid v2 spec; otherwise the issues (an op that cannot apply is
 * reported with its `opIndex`, a spec left invalid by path).
 */
export function applyEdits(
  spec: Spec,
  ops: ReadonlyArray<DashboardEditOperation>,
  options?: DashboardEditOptions,
): ApplyEditsResult {
  const replayed = replay(spec, ops);
  if ("issues" in replayed) return replayed;
  return validateSpecV2(replayed.spec, options);
}

/** The ops that undo `ops` applied to `before`: applying both returns `before`. */
export function invertEdits(
  before: Spec,
  ops: ReadonlyArray<DashboardEditOperation>,
): InvertEditsResult {
  const replayed = replay(before, ops);
  if ("issues" in replayed) return replayed;
  return { ops: [...replayed.inverses].reverse() };
}

// Summaries ----------------------------------------------------------------

/** One change to a widget; `subject` is its quoted title, or null for "it". */
type Phrase = (subject: string | null) => string;

const GRAIN_WORDS: Record<MetricQuery["grain"], string> = {
  day: "daily",
  week: "weekly",
  month: "monthly",
  quarter: "quarterly",
  year: "yearly",
  total: "a single total",
};

const CHART_WORDS: Record<string, string> = {
  line: "a line chart",
  area: "an area chart",
  bar: "a bar chart",
  stacked_bar: "a stacked bar chart",
};

const KEY_WORDS: Record<DashboardWidgetPatchKey, string> = {
  title: "title",
  description: "description",
  data: "data",
  value: "value",
  footnote: "footnote",
  comparison: "comparison",
  definition: "definition",
  chart: "chart type",
  orientation: "orientation",
  x: "x-axis",
  series: "series",
  groupBy: "grouping",
  columns: "columns",
  defaultSort: "default sort",
  emptyMessage: "empty message",
  body: "text",
};

function listWords(items: ReadonlyArray<string>): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function humanize(id: string): string {
  return id.replaceAll(/[._]+/g, " ");
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function widgetName(widget: DashboardWidgetV2): string {
  return widget.title ? `'${widget.title}'` : "the text note";
}

class Names {
  private readonly metrics: ReadonlyMap<string, MetricDefinition>;
  constructor(metrics: ReadonlyArray<MetricDefinition> | undefined) {
    this.metrics = new Map((metrics ?? []).map((metric) => [metric.id, metric]));
  }
  metric(id: string): string {
    return `'${this.metrics.get(id)?.title ?? humanize(id)}'`;
  }
  dimension(metricIds: ReadonlyArray<string>, id: string): string {
    for (const metricId of metricIds) {
      const label = this.metrics.get(metricId)?.dimensions.find((d) => d.id === id)?.label;
      if (label) return lowerFirst(label);
    }
    return humanize(id);
  }
}

function queryPhrases(before: MetricQuery, after: MetricQuery, names: Names): Phrase[] {
  const phrases: Phrase[] = [];
  const it = (subject: string | null) => subject ?? "it";
  if (!same(before.metrics, after.metrics)) {
    const metrics = listWords(after.metrics.map((id) => names.metric(id)));
    phrases.push((s) => `switched ${it(s)} to ${metrics}`);
  }
  if (before.grain !== after.grain) {
    const grain = GRAIN_WORDS[after.grain];
    phrases.push((s) => `changed ${it(s)} to ${grain}`);
  }
  const added = after.dimensions.filter((id) => !before.dimensions.includes(id));
  const removed = before.dimensions.filter((id) => !after.dimensions.includes(id));
  if (added.length > 0) {
    const labels = listWords(added.map((id) => names.dimension(after.metrics, id)));
    phrases.push((s) => `split ${it(s)} by ${labels}`);
  }
  if (removed.length > 0) {
    const labels = listWords(removed.map((id) => names.dimension(before.metrics, id)));
    phrases.push((s) => `stopped splitting ${it(s)} by ${labels}`);
  }
  if (!same(before.filters, after.filters)) {
    phrases.push((s) => (s ? `changed the filters on ${s}` : "changed its filters"));
  }
  if (!same(before.dateRange, after.dateRange)) {
    phrases.push((s) => (s ? `changed the date range of ${s}` : "changed its date range"));
  }
  if (before.compareTo !== after.compareTo) {
    const target = after.compareTo;
    phrases.push((s) =>
      target === undefined
        ? `stopped comparing ${it(s)}`
        : `compared ${it(s)} with ${target === "previous_period" ? "the previous period" : "the same period last year"}`,
    );
  }
  if (before.asOf !== after.asOf) {
    const asOf = after.asOf;
    phrases.push((s) =>
      asOf === "now"
        ? `showed ${it(s)} with today's numbers`
        : asOf === "reported"
          ? `showed ${it(s)} as first reported`
          : `showed ${it(s)} as of ${asOf}`,
    );
  }
  return phrases;
}

function dataPhrases(
  before: DashboardWidgetDataSource,
  after: DashboardWidgetDataSource,
  names: Names,
): Phrase[] {
  if (before.kind === "metric" && after.kind === "metric") {
    return queryPhrases(before.query, after.query, names);
  }
  const target =
    after.kind === "metric"
      ? listWords(after.query.metrics.map((id) => names.metric(id)))
      : `the '${after.dataset}' query`;
  return [(s) => `pointed ${s ?? "it"} at ${target}`];
}

function updatePhrases(
  before: DashboardWidgetV2,
  op: Extract<DashboardEditOperation, { op: "update_widget" }>,
  names: Names,
): Phrase[] {
  const phrases: Phrase[] = [];
  const previous = before as Record<string, unknown>;
  const other: string[] = [];
  for (const key of Object.keys(op.set) as DashboardWidgetPatchKey[]) {
    const value = op.set[key];
    if (same(previous[key], value)) continue;
    if (key === "data" && "data" in before && op.set.data) {
      phrases.push(...dataPhrases(before.data, op.set.data, names));
    } else if (key === "title") {
      phrases.push((s) => `renamed ${s ?? "it"} to '${String(value)}'`);
    } else if (key === "chart") {
      const chart = CHART_WORDS[String(value)] ?? "another chart";
      phrases.push((s) => `made ${s ?? "it"} ${chart}`);
    } else {
      other.push(KEY_WORDS[key]);
    }
  }
  if (other.length > 0) {
    const words = listWords(other);
    phrases.push((s) => (s ? `updated the ${words} of ${s}` : `updated its ${words}`));
  }
  const removed = (op.unset ?? []).filter((key) => key in previous).map((key) => KEY_WORDS[key]);
  if (removed.length > 0) {
    const words = listWords(removed);
    phrases.push((s) => (s ? `removed the ${words} from ${s}` : `removed its ${words}`));
  }
  return phrases;
}

function parameterName(parameter: { readonly label: string }): string {
  return `the '${parameter.label}' filter`;
}

/**
 * A short plain-language summary of `ops` applied to `before`, e.g. "Changed
 * 'Missed calls' to weekly and split it by navigator". Changes to one widget
 * are combined into one sentence; sentences are joined with ". ". Ops that do
 * not apply are skipped.
 */
export function summarizeEdits(
  before: Spec,
  ops: ReadonlyArray<DashboardEditOperation>,
  options?: DashboardEditOptions,
): string {
  const names = new Names(options?.metrics);
  type Entry = { readonly text: string } | { readonly subject: string; phrases: Phrase[] };
  const entries: Entry[] = [];
  const widgetEntries = new Map<string, { subject: string; phrases: Phrase[] }>();
  const added = new Set<string>();
  const widgetPhrases = (widget: DashboardWidgetV2, phrases: ReadonlyArray<Phrase>) => {
    if (phrases.length === 0 || added.has(widget.id)) return;
    let entry = widgetEntries.get(widget.id);
    if (!entry) {
      entry = { subject: widgetName(widget), phrases: [] };
      widgetEntries.set(widget.id, entry);
      entries.push(entry);
    }
    entry.phrases.push(...phrases);
  };

  let spec = before;
  for (const op of ops) {
    const result = step(spec, op);
    if ("message" in result) continue;
    const found = "widgetId" in op ? locate(spec.rows, op.widgetId) : undefined;
    switch (op.op) {
      case "add_widget":
        added.add(op.widget.id);
        entries.push({ text: `added ${widgetName(op.widget)}` });
        break;
      case "remove_widget":
        entries.push({ text: `removed ${widgetName(found!.widget)}` });
        break;
      case "move_widget":
        entries.push({
          text: `moved ${widgetName(found!.widget)}${op.newRow ? " to a new row" : ""}`,
        });
        break;
      case "resize_widget": {
        const from = found!.widget.span;
        const to = op.span;
        widgetPhrases(found!.widget, [
          (s) =>
            to === null
              ? s
                ? `reset the width of ${s}`
                : "reset its width"
              : `made ${s ?? "it"} ${from === undefined || to > from ? "wider" : "narrower"}`,
        ]);
        break;
      }
      case "update_widget":
        widgetPhrases(found!.widget, updatePhrases(found!.widget, op, names));
        break;
      case "set_title":
        entries.push({ text: `renamed the dashboard to '${op.title}'` });
        break;
      case "set_description":
        entries.push({
          text:
            op.description === null
              ? "removed the dashboard description"
              : "updated the dashboard description",
        });
        break;
      case "add_parameter":
        entries.push({ text: `added ${parameterName(op.parameter)}` });
        break;
      case "update_parameter":
        entries.push({ text: `changed ${parameterName(op.parameter)}` });
        break;
      case "remove_parameter": {
        const parameter = spec.parameters.find((candidate) => candidate.id === op.parameterId)!;
        entries.push({ text: `removed ${parameterName(parameter)}` });
        break;
      }
      case "add_dataset":
        entries.push({ text: `added the '${op.dataset.id}' query` });
        break;
      case "update_dataset":
        entries.push({ text: `changed the '${op.dataset.id}' query` });
        break;
      case "remove_dataset":
        entries.push({ text: `removed the '${op.datasetId}' query` });
        break;
    }
    spec = result.spec;
  }

  if (entries.length === 0) return "No changes";
  return entries
    .map((entry) => {
      if ("text" in entry) return capitalize(entry.text);
      const [first, ...rest] = entry.phrases;
      return capitalize(listWords([first!(entry.subject), ...rest.map((phrase) => phrase(null))]));
    })
    .join(". ");
}
